/**
 * Generic dark card with optional title and trailing slot.
 *
 * Used to group every block in a tab so spacing & corner radii stay
 * consistent. Compose freely with `OcppEmptyState`, `OcppLoadingState`,
 * tables, or arbitrary children.
 */

import React from 'react';
import { StyleSheet, Text, View, ViewStyle } from 'react-native';

import { OcppTheme } from './OcppTheme';

export interface OcppSectionCardProps {
  title?: string;
  subtitle?: string;
  trailing?: React.ReactNode;
  children?: React.ReactNode;
  style?: ViewStyle;
  contentStyle?: ViewStyle;
}

const OcppSectionCard: React.FC<OcppSectionCardProps> = ({
  title,
  subtitle,
  trailing,
  children,
  style,
  contentStyle,
}) => {
  return (
    <View style={[styles.card, style]}>
      {(title || subtitle || trailing) && (
        <View style={styles.header}>
          <View style={styles.headerText}>
            {title ? <Text style={styles.title}>{title}</Text> : null}
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>
          {trailing ? <View style={styles.trailing}>{trailing}</View> : null}
        </View>
      )}
      <View style={[styles.content, contentStyle]}>{children}</View>
    </View>
  );
};

export default OcppSectionCard;

const styles = StyleSheet.create({
  card: {
    backgroundColor: OcppTheme.surface,
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 14,
    paddingHorizontal: 14,
    gap: 8,
  },
  headerText: {
    flex: 1,
  },
  title: {
    color: OcppTheme.textPrimary,
    fontSize: 16,
    fontWeight: '700',
  },
  subtitle: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginTop: 4,
    lineHeight: 16,
  },
  trailing: {
    marginLeft: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  content: {
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 14,
  },
});
